import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { erc20Abi, formatUnits, type Address } from "viem";
import type { RedKeel } from "../config/networks";
import { usePools, type Pool, type Token } from "../hooks/usePools";
import { useCrear } from "../hooks/useCrear";
import { esEstable, precioATick, semianchoEnTicks, sueloTick, tickAPrecio, vistaPrecio } from "../lib/precio";
import { Banda } from "./Banda";
import { GraficoRango } from "./GraficoRango";
import { useHistPrecio } from "../hooks/useHistPrecio";
import { ElegirPool } from "./ElegirPool";
import { FichaPool } from "./FichaPool";
import { Modal, Pasos, Hueco, Giro } from "./Modal";
import { ModalOperacion, type EstadoOp } from "./ModalOperacion";
import { num } from "../lib/formato";
import { useT } from "../lib/i18n";

const POOL_ABI = [
  // slot0 truncado a (sqrtPriceX96, tick): Aerodrome Slipstream devuelve 6 campos (sin feeProtocol)
  // y Uniswap/Pancake 7; con 2 vale para los dos (viem lee los primeros e ignora el resto). Sin
  // esto, leer el precio de una pool Slipstream reventaba y el modal de crear no cargaba el precio.
  { type: "function", name: "slot0", stateMutability: "view", inputs: [],
    outputs: [{ type: "uint160" }, { type: "int24" }] },
  // El tickSpacing REAL de la pool. En Uniswap se deriva del fee (100→1, 500→10…), pero Aerodrome
  // Slipstream usa fees dinámicas (250, 450…) que no están en esa tabla: hay que leerlo de la pool.
  { type: "function", name: "tickSpacing", stateMutability: "view", inputs: [], outputs: [{ type: "int24" }] },
] as const;

const ANCHOS = [1, 2, 3, 5, 10, 20];
const SPACING_POR_COMISION: Record<number, number> = { 100: 1, 500: 10, 3000: 60, 10000: 200 };
const TITULOS = ["En qué", "Cuánto", "El rango", "Confirmar"];

export function CrearModal({
  abierto, cerrar, red, alAbrirVault, copiar,
}: {
  abierto: boolean;
  cerrar: () => void;
  red: RedKeel;
  alAbrirVault?: (v: Address) => void;
  /** «Copiar» una piscina de la portada: llega su pool y su ±% de rango, y el modal nace con eso
   *  puesto, saltando directo a la cantidad. */
  copiar?: { pool: Address; pct: number };
}) {
  const t = useT();
  const pools = usePools(red);
  // Solo se crean piscinas con un estable: la bolsa de gas vive en uno de los dos tokens y el robot
  // cobra el gas en dolares. Sin estable (WETH/cbBTC…) la bolsa no cuadra y el vault nunca se reajusta.
  const creables = useMemo(
    () => pools.data?.filter((p) => esEstable(p.token0.symbol) || esEstable(p.token1.symbol)),
    [pools.data],
  );
  const { address } = useAccount();
  const config = useConfig();

  const [paso, setPaso] = useState(0);
  const [elegida, setElegida] = useState<Pool | null>(null);
  const [cantidad, setCantidad] = useState("");
  // Bolsa de gas por defecto: subida de 2 a 10 al pasar el cooldown a 300 s. Con reajustes hasta 12x
  // mas frecuentes, una bolsa de 2 se vaciaba en <2 h y el vault se quedaba SIN-GAS (deja de reajustar).
  // Es solo la sugerencia inicial: el usuario la edita, y lo que no se gasta se devuelve entero.
  const [gas, setGas] = useState("10");
  // Si el usuario aún no ha tocado la bolsa de gas, le ponemos un valor por defecto SENSATO según el
  // token: ~10 si deposita el estable (≈10 $), y 0 si deposita el activo (10 del activo sería una
  // barbaridad — p.ej. 10 WBTC). Así deja de salir un gas absurdo al cambiar de token.
  const [gasTocado, setGasTocado] = useState(false);
  const [depToken0, setDepToken0] = useState(true);
  const [modoRango, setModoRango] = useState<"pct" | "manual">("pct");
  const [pct, setPct] = useState(3);
  const [manMin, setManMin] = useState("");
  const [manMax, setManMax] = useState("");
  const [reservaPct, setReservaPct] = useState(0);
  const [modoRein, setModoRein] = useState(1);
  const [reinPct, setReinPct] = useState(20);
  const [caidaPct, setCaidaPct] = useState(5);
  const [reinvertir, setReinvertir] = useState(false);

  const { crear, pasos: pasosTx, error, vaultCreado, ocupado, limpiar } = useCrear(red);

  const estableEsToken0 = elegida ? esEstable(elegida.token0.symbol) && !esEstable(elegida.token1.symbol) : true;
  const tokenDep: Token | null = elegida ? (depToken0 ? elegida.token0 : elegida.token1) : null;
  const esReferencia = depToken0 === estableEsToken0;

  useEffect(() => { if (elegida) setDepToken0(estableEsToken0); }, [elegida, estableEsToken0]);

  // Bolsa de gas por defecto según el token elegido, mientras el usuario no la haya tocado.
  useEffect(() => { if (!gasTocado) setGas(esReferencia ? "10" : "0"); }, [esReferencia, gasTocado]);

  // COPIAR UNA PISCINA de la portada: cuando el modal se abre con `copiar`, se preselecciona su pool
  // y su ±% de rango, y se salta al paso de la cantidad. Solo mientras no haya nada elegido, para no
  // pisar al usuario si luego cambia el pool a mano. El resto (gas, reserva…) queda por
  // defecto y editable — se copia la piscina, no toda la configuración.
  useEffect(() => {
    if (!abierto || !copiar || !creables || elegida) return;
    const p = creables.find((x) => x.pool.toLowerCase() === copiar.pool.toLowerCase());
    if (!p) return;
    setElegida(p);
    setModoRango("pct");
    setPct(copiar.pct);
    setPaso(1);
  }, [abierto, copiar, creables, elegida]);

  const precio = useQuery({
    queryKey: ["precio", red.clave, elegida?.pool],
    enabled: !!elegida && abierto,
    refetchInterval: 15_000,
    queryFn: async () => {
      const client = getPublicClient(config, { chainId: red.idCadena })!;
      const [s, ts] = await Promise.all([
        client.readContract({ address: elegida!.pool, abi: POOL_ABI, functionName: "slot0" }),
        client.readContract({ address: elegida!.pool, abi: POOL_ABI, functionName: "tickSpacing" }),
      ]);
      const sp = Number(s[0]) / 2 ** 96;
      return {
        price0in1: sp * sp * 10 ** (elegida!.token0.decimals - elegida!.token1.decimals),
        tick: s[1],
        spacing: Number(ts),
      };
    },
  });

  // El tickSpacing REAL de la pool (leído arriba). Mientras carga, se estima por el fee como
  // respaldo (correcto en Uniswap; en Slipstream el valor bueno llega con la query).
  const spacing = precio.data?.spacing ?? (elegida ? (SPACING_POR_COMISION[elegida.fee] ?? 60) : 60);

  const saldo = useQuery({
    queryKey: ["saldo", red.clave, tokenDep?.address, address],
    enabled: !!address && !!tokenDep && abierto,
    staleTime: 15_000,
    // El saldo "a veces no salía": una lectura fallida (RPC con hipo, cliente aún sin montar) dejaba
    // el balance en blanco sin reintentar. Ahora reintenta con espera creciente y refresca al volver
    // a la pestaña; si aun así falla, la UI ofrece "reintentar" en vez de quedarse muda.
    retry: 4,
    retryDelay: (i) => Math.min(1000 * 2 ** i, 6000),
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const client = getPublicClient(config, { chainId: red.idCadena });
      if (!client) throw new Error("Sin conexión con la red");
      const b = await client.readContract({
        address: tokenDep!.address, abi: erc20Abi, functionName: "balanceOf", args: [address!],
      });
      return Number(formatUnits(b, tokenDep!.decimals));
    },
  });

  const hist = useHistPrecio(red, elegida?.pool, abierto && paso === 2 && !!elegida);

  const vista = elegida && precio.data
    ? vistaPrecio(precio.data.price0in1, elegida.token0.symbol, elegida.token1.symbol)
    : null;

  // Los DOS semianchos del rango, en ticks del espacio price0in1 (el que usa el contrato). En "±%"
  // son iguales → banda simétrica → `open`. En "precios exactos" se miden contra el precio de AHORA y
  // pueden diferir → `openAsym`, que respeta la banda descentrada tal cual y la conserva al re-centrar.
  const semis = useMemo(() => {
    if (!elegida || !precio.data) return null;
    const d0 = elegida.token0.decimals, d1 = elegida.token1.decimals;
    if (modoRango === "pct") {
      const s = semianchoEnTicks(1 - pct / 100, 1 + pct / 100, d0, d1, spacing);
      return { below: s, above: s };
    }
    const a = Number(manMin), b = Number(manMax);
    if (!(a > 0 && b > a)) return null;
    // en price0in1: con la vista invertida (el estable es token0) el mín/máx del usuario se cruzan
    const p1 = vista?.invertido ? 1 / b : a; // borde INFERIOR en price0in1
    const p2 = vista?.invertido ? 1 / a : b; // borde SUPERIOR en price0in1
    const c = precio.data.tick;
    const clamp = (n: number) => Math.min(20000, Math.max(spacing, Math.round(n)));
    // Cada lado es la distancia en ticks del precio de ahora a ese borde. Acotado a [tickSpacing,
    // 20000] como exige el contrato: si el usuario pone un borde al otro lado del precio, ese lado se
    // queda en el mínimo de un tickSpacing (y el «Quedará en» de abajo lo enseña).
    const below = clamp(c - precioATick(p1, d0, d1));
    const above = clamp(precioATick(p2, d0, d1) - c);
    return { below, above };
  }, [elegida, precio.data, modoRango, pct, manMin, manMax, spacing, vista?.invertido]);

  const rango = useMemo(() => {
    if (!elegida || !precio.data || !semis) return null;
    const d0 = elegida.token0.decimals, d1 = elegida.token1.decimals;
    const c = precio.data.tick;
    const lo = tickAPrecio(sueloTick(c - semis.below, spacing), d0, d1);
    const hi = tickAPrecio(sueloTick(c + semis.above, spacing), d0, d1);
    const paso1 = tickAPrecio(sueloTick(c, spacing) + spacing, d0, d1) - tickAPrecio(sueloTick(c, spacing), d0, d1);
    return vista?.invertido
      ? { lo: 1 / hi, hi: 1 / lo, paso: Math.abs(1 / hi - 1 / (hi + paso1)) }
      : { lo, hi, paso: paso1 };
  }, [elegida, precio.data, semis, spacing, vista?.invertido]);

  useEffect(() => {
    if (modoRango !== "manual" || manMin || manMax || !elegida || !precio.data || !vista) return;
    const d0 = elegida.token0.decimals, d1 = elegida.token1.decimals;
    const semi = semianchoEnTicks(1 - pct / 100, 1 + pct / 100, d0, d1, spacing);
    const c = precio.data.tick;
    const lo = tickAPrecio(sueloTick(c - semi, spacing), d0, d1);
    const hi = tickAPrecio(sueloTick(c + semi, spacing), d0, d1);
    const [a, b] = vista.invertido ? [1 / hi, 1 / lo] : [lo, hi];
    setManMin(Number(a.toPrecision(6)).toString());
    setManMax(Number(b.toPrecision(6)).toString());
  }, [modoRango, manMin, manMax, elegida, precio.data, vista, pct, spacing]);

  /** A que distancia del precio de ahora queda un precio, en %. `null` si aun no hay referencia. */
  const pctDe = (p: number) =>
    vista && Number.isFinite(p) && p > 0 && vista.px > 0 ? (p / vista.px - 1) * 100 : null;
  const fmtPct = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(v > -10 && v < 10 ? 1 : 0)}%`;

  const inv = Number(cantidad) || 0;
  const gasN = Number(gas) || 0;
  const reservaN = esReferencia ? (inv * reservaPct) / 100 : 0;
  const total = inv + gasN;
  const sinSaldo = saldo.data != null && total > saldo.data;
  // crear + aprobar + depositar + abrir + activar el robot. El approve puede saltarse si ya hay
  // permiso, pero se cuenta: prometer menos firmas de las que salen molesta mas que al reves.
  const firmas =
    5 +
    (modoRein !== 0 && reservaPct > 0 && esReferencia ? 1 : 0) +
    (reinvertir ? 1 : 0);

  function reiniciar() {
    setPaso(0); setElegida(null); setCantidad(""); setGas("10"); setGasTocado(false);
    setModoRango("pct"); setPct(3); setManMin(""); setManMax("");
    setReservaPct(0); setModoRein(1); setReinvertir(false);
  }

  const puedeSeguir =
    paso === 0 ? !!elegida :
    paso === 1 ? inv > 0 && !sinSaldo :
    paso === 2 ? !!semis && !!rango :
    true;

  // ---- proceso: mientras se crea (varias firmas encadenadas) o si algo falla, el mismo modal de
  // proceso que el resto de operaciones (logo → cargando con los pasos → error). El éxito tiene su
  // propia pantalla (más abajo, con el enlace a la piscina).
  const opCrear: EstadoOp | null = ocupado
    ? {
        fase: "cargando",
        titulo: t("Abriendo la piscina"),
        sub: t("Confírmalo en tu wallet: son varias firmas encadenadas."),
        pasos: pasosTx,
      }
    : error
      ? { fase: "error", titulo: "", mensaje: error, ...(pasosTx.length ? { pasos: pasosTx } : {}) }
      : null;
  if (abierto && (ocupado || error)) {
    // Mientras trabaja no se puede cerrar (la operación sigue igual); al fallar, cerrar limpia y
    // devuelve al formulario con los datos puestos para reintentar.
    return <ModalOperacion estado={opCrear} cerrar={ocupado ? () => {} : limpiar} red={red} />;
  }

  // ---------------------------------------------------------------- creada
  if (vaultCreado) {
    return (
      <Modal abierto={abierto} cerrar={() => { cerrar(); reiniciar(); }} titulo={t("Tu piscina está abierta")}
        sub={t("El dinero ya está en el rango y el robot la gestiona desde ahora.")}
        pie={
          <>
            <button className="btn" onClick={() => { cerrar(); reiniciar(); }}>{t("Cerrar")}</button>
            <button className="btn principal" onClick={() => { alAbrirVault?.(vaultCreado); cerrar(); reiniciar(); }}>
              {t("Ver mi piscina")}
            </button>
          </>
        }>
        <div className="exito">
          <div className="tic" aria-hidden>✓</div>
          <p className="detalle mono">{vaultCreado}</p>
          {rango && vista && <Banda precio={vista.px} lo={rango.lo} hi={rango.hi} alto={60} />}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      abierto={abierto}
      cerrar={() => { if (!ocupado) { cerrar(); } }}
      titulo={t("Abrir una piscina")}
      sub={elegida ? t("{pair} · comisión {fee}%", { pair: elegida.pair, fee: elegida.fee / 10000 }) : t("Cuatro pasos. Puedes volver atrás en cualquiera.")}
      icono={
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 14c1.5 0 1.5 1.4 3 1.4S8.5 14 10 14s1.5 1.4 3 1.4S15.5 14 17 14s1.5 1.4 3 1.4" />
          <path d="M3 18c1.5 0 1.5 1.4 3 1.4S8.5 18 10 18s1.5 1.4 3 1.4S15.5 18 17 18s1.5 1.4 3 1.4" />
          <path d="M12 3v7M9 6l3-3 3 3" />
        </svg>
      }
      ancho={760}
      pie={
        <>
          <button className="btn" onClick={() => (paso === 0 ? cerrar() : setPaso(paso - 1))} disabled={ocupado}>
            {paso === 0 ? t("Cancelar") : t("← Atrás")}
          </button>
          {paso < 3 ? (
            <button className="btn principal" onClick={() => setPaso(paso + 1)} disabled={!puedeSeguir}>
              {t("Seguir →")}
            </button>
          ) : (
            <button className="btn principal" disabled={ocupado || !puedeSeguir || sinSaldo}
              onClick={() => crear({
                pool: elegida!, cantidad, gas, below: semis!.below, above: semis!.above, spacing,
                depositaToken0: depToken0,
                reservaPct: esReferencia ? reservaPct : 0,
                modoReinyeccion: esReferencia && reservaPct > 0 ? modoRein : 0,
                reinyeccionPct: reinPct, caidaPct, reinvertir,
              })}>
              {ocupado ? <Giro texto={t("Abriendo…")} /> : t("Abrir · {firmas} firmas", { firmas })}
            </button>
          )}
        </>
      }
    >
      <Pasos pasos={TITULOS.map((x) => t(x))} actual={paso} />

      {/* Datos reales de la pool elegida: cabecera que acompaña los pasos de configuración para no
          perder de vista en qué se invierte. Solo con pool elegida (a partir del paso 2). */}
      {elegida && paso > 0 && <FichaPool pool={elegida} red={red} />}

      {/* ---------------------------------------------------------------- 1 · en que */}
      {paso === 0 && (
        <>
          {pools.isPending && <Hueco alto={130} n={2} />}
          {pools.isError && (
            <div className="aviso error">
              <p>{t("No pude leer las pools de {etiqueta}.", { etiqueta: red.etiqueta })}</p>
              <p className="detalle">{pools.error.message}</p>
              <button className="btn" onClick={() => pools.refetch()}>{t("Reintentar")}</button>
            </div>
          )}
          {creables && (
            <ElegirPool pools={creables} red={red} elegida={elegida}
              alElegir={(p) => { setElegida(p); setManMin(""); setManMax(""); setPaso(1); }}
              deshabilitado={ocupado} alAbrirVault={alAbrirVault} />
          )}
        </>
      )}

      {/* ---------------------------------------------------------------- 2 · cuanto */}
      {paso === 1 && elegida && tokenDep && (
        <div className="hoja">
          {/* El token PRIMERO: define de qué saldo hablamos. Así "tienes X" de abajo no confunde. */}
          <label className="campo">
            <span>{t("Con qué token")} <em>{t("usa lo que ya tengas")}</em></span>
            <select value={depToken0 ? "0" : "1"} onChange={(e) => setDepToken0(e.target.value === "0")}>
              <option value={estableEsToken0 ? "0" : "1"}>
                {t("{symbol} · el estable", { symbol: (estableEsToken0 ? elegida.token0 : elegida.token1).symbol })}
              </option>
              <option value={estableEsToken0 ? "1" : "0"}>
                {t("{symbol} · el activo", { symbol: (estableEsToken0 ? elegida.token1 : elegida.token0).symbol })}
              </option>
            </select>
          </label>

          <label className="campo">
            <span>
              {t("Cuánto inviertes")}
              {/* SALDO ROBUSTO: cargando / error+reintentar / dato+MÁX. Nunca se queda en blanco sin decir por qué. */}
              {saldo.isPending && <em> · <Giro texto={t("leyendo tu saldo…")} /></em>}
              {saldo.isError && !saldo.isPending && (
                <em> · {t("no pude leer tu saldo")}{" "}
                  <button className="enlace" style={{ padding: 0 }} onClick={() => saldo.refetch()}>{t("reintentar")}</button>
                </em>
              )}
              {saldo.data != null && (
                <em>
                  {" "}· {t("tienes {saldo} {symbol}", { saldo: num(saldo.data, 4), symbol: tokenDep.symbol })}{" "}
                  <button className="enlace" style={{ padding: 0 }}
                    onClick={() => setCantidad(String(Math.max(0, saldo.data! - gasN)))}>{t("MÁX")}</button>
                </em>
              )}
            </span>
            <input type="number" value={cantidad} onChange={(e) => setCantidad(e.target.value)}
              placeholder="500" min="0" step="any" autoFocus data-foco />
          </label>

          <label className="campo">
            <span>{t("Bolsa de gas")} <em>{t("opcional")}</em></span>
            <input type="number" value={gas}
              onChange={(e) => { setGas(e.target.value); setGasTocado(true); }} min="0" step="any" />
            <small className="pista">
              {t("Una reserva en")} <strong>{tokenDep.symbol}</strong> {t("que el robot gasta para pagar el gas de cada reajuste.")} <strong>{t("Lo que no gaste se te devuelve.")}</strong> {t("Si no lo tienes claro, déjala como está.")}
            </small>
          </label>

          {/* TOTAL EN VIVO: deja claro que inversión + gas = lo que sale de tu wallet, y si cabe. */}
          <div className="crear-total">
            <div className="fila"><span>{t("Inviertes")}</span><b>{num(inv, 4)} {tokenDep.symbol}</b></div>
            <div className="fila"><span>{t("Bolsa de gas")}</span><b>{num(gasN, 4)} {tokenDep.symbol}</b></div>
            <div className="fila total">
              <span>{t("Sale de tu wallet")}</span>
              <b className={sinSaldo ? "malo" : ""}>{num(total, 4)} {tokenDep.symbol}</b>
            </div>
          </div>

          {sinSaldo && (
            <p className="detalle malo">
              {t("Te faltan {falta} {symbol} para cubrir la inversión más la bolsa de gas. Baja la cantidad o pulsa MÁX.", { falta: num(total - (saldo.data ?? 0), 4), symbol: tokenDep.symbol })}
            </p>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- 3 · rango */}
      {paso === 2 && elegida && (
        <div className="hoja">
          {/* EL PRECIO DE AHORA, ARRIBA Y GRANDE. Sin esta referencia, escribir un minimo y un
              maximo es adivinar: el chip dentro de la banda queda pequeño y llega tarde. */}
          <div className="precio-ahora">
            <span className="k">{t("Precio ahora")}</span>
            {precio.data && vista ? (
              <span className="v">
                {num(vista.px, vista.px < 1 ? 6 : 2)}{" "}
                <i>{t("{unidad} por {activo}", { unidad: vista.unidad, activo: vista.activo })}</i>
              </span>
            ) : <Giro />}
            {precio.isFetching && precio.data && <span className="vivo" title={t("actualizando")} aria-hidden />}
          </div>

          {/* GRÁFICO: histórico de precio (reconstruido de swaps) con las líneas del rango encima,
              que se mueven al cambiar el ancho, y el APR estimado por rango + lo invertido. */}
          <GraficoRango
            hist={hist.data ?? null}
            cargando={hist.isPending && !!elegida}
            invVista={!!vista?.invertido}
            unidad={vista?.unidad ?? ""}
            token0={elegida.token0.symbol}
            token1={elegida.token1.symbol}
            rawLo={rango && vista ? (vista.invertido ? 1 / rango.hi : rango.lo) : null}
            rawHi={rango && vista ? (vista.invertido ? 1 / rango.lo : rango.hi) : null}
            rawPx={precio.data?.price0in1 ?? null}
            aprBase={elegida.aprPct ?? 0}
            inv={inv}
            tokenSym={tokenDep?.symbol ?? ""}
          />

          <div className="conmutador">
            <button className={`ancho ${modoRango === "pct" ? "activo" : ""}`}
              onClick={() => setModoRango("pct")}>{t("Por porcentaje")}</button>
            <button className={`ancho ${modoRango === "manual" ? "activo" : ""}`}
              onClick={() => setModoRango("manual")}>{t("Precios exactos")}</button>
          </div>

          {modoRango === "pct" ? (
            <div className="campo">
              <span>{t("Ancho")} <em>{t("±{pct}% alrededor del precio", { pct })}</em></span>
              <div className="anchos">
                {ANCHOS.map((a) => (
                  <button key={a} className={`ancho ${pct === a ? "activo" : ""}`}
                    onClick={() => setPct(a)}>±{a}%</button>
                ))}
              </div>
              <input type="range" min="0.5" max="30" step="0.5" value={pct}
                onChange={(e) => setPct(Number(e.target.value))} />
            </div>
          ) : (
            <>
              <div className="dos">
                <label className="campo">
                  <span>
                    {t("Precio mínimo")} <em>{t("en {unidad}", { unidad: vista?.unidad ?? "" })}</em>
                    {pctDe(Number(manMin)) != null && (
                      <b className={`pct ${pctDe(Number(manMin))! < 0 ? "baja" : "sube"}`}>
                        {fmtPct(pctDe(Number(manMin))!)}
                      </b>
                    )}
                  </span>
                  <input type="number" value={manMin} onChange={(e) => setManMin(e.target.value)} step="any" min="0" />
                </label>
                <label className="campo">
                  <span>
                    {t("Precio máximo")} <em>{t("en {unidad}", { unidad: vista?.unidad ?? "" })}</em>
                    {pctDe(Number(manMax)) != null && (
                      <b className={`pct ${pctDe(Number(manMax))! < 0 ? "baja" : "sube"}`}>
                        {fmtPct(pctDe(Number(manMax))!)}
                      </b>
                    )}
                  </span>
                  <input type="number" value={manMax} onChange={(e) => setManMax(e.target.value)} step="any" min="0" />
                </label>
              </div>
              {Number(manMin) > 0 && Number(manMax) <= Number(manMin) && (
                <p className="detalle malo">{t("El máximo tiene que ser mayor que el mínimo.")}</p>
              )}
              <p className="detalle">
                {t("Tu rango se respeta")} <strong>{t("tal cual lo escribes")}</strong>{t(", con más hueco a un lado si quieres. El robot lo cuadra a la rejilla de la pool y, al re-centrarse, lo desliza con el precio conservando su forma. Abajo ves dónde queda exacto.")}
              </p>
            </>
          )}

          {precio.isPending && <Hueco alto={96} />}
          {precio.data && rango && vista && (
            <div>
              {/* Lo que va a quedar DE VERDAD, con su porcentaje. Con `openAsym` el rango es el que
                  escribió el dueño; la única diferencia con lo tecleado es el cuadre a la rejilla de
                  la pool (tickSpacing). Se enseña igualmente para que no haya sorpresas. */}
              <div className="quedara">
                <span className="k">{t("Quedará en")}</span>
                <span className="v">
                  {num(rango.lo, rango.lo < 1 ? 6 : 2)} – {num(rango.hi, rango.hi < 1 ? 6 : 2)} {vista.unidad}
                </span>
                <span className="pcts">
                  <b className="pct baja">{fmtPct(pctDe(rango.lo)!)}</b>
                  <b className="pct sube">{fmtPct(pctDe(rango.hi)!)}</b>
                </span>
              </div>

              <p className="detalle" style={{ marginTop: 10 }}>
                {t("Cobras mientras {activo} esté entre esos dos precios, en {unidad}. Más estrecho cobra más, pero se sale antes — y cada salida gasta de tu bolsa de gas.", { activo: vista.activo, unidad: vista.unidad })}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---------------------------------------------------------------- 4 · confirmar */}
      {paso === 3 && elegida && tokenDep && (
        <div className="hoja">
          <details className="bloque">
            <summary>{t("Promediar a la baja")} <span className="opcional">{t("opcional")}</span></summary>
            <div className="cuerpo">
              <p className="detalle" style={{ margin: 0 }}>
                {t("Aparta una parte y el robot la va soltando cuando el precio cae, para comprar más barato.")}{!esReferencia && <> <strong>{t("Solo con el estable")}</strong>{t(": la reserva vive en él.")}</>}
              </p>
              <label className="campo">
                <span>{t("Reserva")} <em>{reservaPct}%{reservaN > 0 ? ` · ${num(reservaN, 2)} ${tokenDep.symbol}` : ""}</em></span>
                <input type="range" min="0" max="90" step="5" value={reservaPct}
                  onChange={(e) => setReservaPct(Number(e.target.value))} disabled={!esReferencia} />
              </label>
              {reservaPct > 0 && esReferencia && (
                <>
                  <label className="campo">
                    <span>{t("Cuándo soltarla")}</span>
                    <select value={modoRein} onChange={(e) => setModoRein(Number(e.target.value))}>
                      <option value={1}>{t("En cada reajuste del rango")}</option>
                      <option value={2}>{t("Cada vez que el precio cae otro tanto")}</option>
                      <option value={0}>{t("Nunca — se queda quieta")}</option>
                    </select>
                  </label>
                  <div className="dos">
                    <label className="campo">
                      <span>{t("Cuánto suelta")} <em>{t("% de la reserva")}</em></span>
                      <input type="number" value={reinPct} min="1" max="100"
                        onChange={(e) => setReinPct(Number(e.target.value))} />
                    </label>
                    {modoRein === 2 && (
                      <label className="campo">
                        <span>{t("Por cada caída de")} <em>%</em></span>
                        <input type="number" value={caidaPct} min="0.5" step="0.5"
                          onChange={(e) => setCaidaPct(Number(e.target.value))} />
                      </label>
                    )}
                  </div>
                  {modoRein === 0 && (
                    <p className="detalle malo">{t("Con «nunca» la reserva se queda parada y no hace nada.")}</p>
                  )}
                </>
              )}
              <label className="ck">
                <input type="checkbox" checked={reinvertir} onChange={(e) => setReinvertir(e.target.checked)} />
                <span>{t("Reinvertir las ganancias en vez de apartarlas")}</span>
              </label>
            </div>
          </details>

          {rango && vista && <Banda precio={vista.px} lo={rango.lo} hi={rango.hi} alto={54} />}

          <div className="resumen-crear">
            <div><span className="k">{t("Sale de tu wallet")}</span>
              <span className="v">{num(total, 4)} {tokenDep.symbol}</span></div>
            <div><span className="k">{t("Trabajando en el rango")}</span>
              <span className="v">{num(inv - reservaN, 4)} {tokenDep.symbol}</span></div>
            {reservaN > 0 && (
              <div><span className="k">{t("Apartado para promediar")}</span>
                <span className="v">{num(reservaN, 4)} {tokenDep.symbol}</span></div>
            )}
            <div><span className="k">{t("Bolsa de gas")}</span>
              <span className="v">{num(gasN, 4)} {tokenDep.symbol}</span></div>
            <div><span className="k">{t("Comisión de Keellabs")}</span>
              <span className="v">{t("10% de las ganancias")}</span></div>
            <div><span className="k">{t("Firmas en tu wallet")}</span><span className="v">{firmas}</span></div>
          </div>

          <p className="detalle">
            {t("Tu dinero va a un vault que es")} <strong>{t("tuyo")}</strong>{t(": el robot solo puede reajustar el rango, no sacarlo ni cambiar tus ajustes. Puedes retirarlo todo cuando quieras.")}
          </p>
          {/* El progreso (pasos encadenados) y el error se muestran en el modal de proceso, arriba. */}
        </div>
      )}
    </Modal>
  );
}
